/**
 * WeatherOverlay.tsx
 * 
 * Real-time weather overlay for map views
 * Shows wind speed, flash flood warnings, and temperature
 * Critical for desert safety - flash floods are extremely dangerous
 * 
 * Uses Google Weather API via server-side proxy
 */

import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tooltip, IconButton, Collapse, Chip, Alert, LinearProgress } from '@mui/material';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FloodIcon from '@mui/icons-material/Flood';
import StormIcon from '@mui/icons-material/Storm';
import { getCyberColors } from '../theme/cyberColors';

const cyber = getCyberColors(true);

interface WeatherData {
  temp: number;
  feelsLike: number;
  humidity: number;
  windSpeed: number;
  windGust?: number;
  windDirection: number;
  windCardinal: string;
  description: string;
  icon: string;
  visibility: number;
  clouds: number;
  uvIndex: number;
  pressure: number;
  precipProbability: number;
  thunderstormProbability: number;
  isDaytime: boolean;
  heatIndex?: number;
  windChill?: number;
  // Precipitation data (from hourly forecast if available)
  rain1h?: number;
  rain3h?: number;
  alerts?: WeatherAlert[];
}

interface WeatherAlert {
  event: string;
  sender: string;
  start: number;
  end: number;
  description: string;
  tags: string[];
}

interface WeatherOverlayProps {
  latitude: number;
  longitude: number;
  darkMode?: boolean;
  expanded?: boolean;
  onToggleExpanded?: () => void;
}

// Wind direction to compass
const getWindDirection = (deg: number): string => {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return directions[Math.round(deg / 22.5) % 16];
};

// Wind speed severity with oilfield-specific thresholds
// 20 mph = Yellow (caution), 25 mph = Orange (warning), 30+ mph = Red (danger)
const getWindSeverity = (mph: number): { color: string; label: string; level: 'calm' | 'moderate' | 'caution' | 'warning' | 'danger' | 'extreme' } => {
  if (mph >= 58) return { color: '#ff0000', label: 'HURRICANE FORCE', level: 'extreme' };
  if (mph >= 30) return { color: '#ef4444', label: 'DANGER - HIGH WIND', level: 'danger' };
  if (mph >= 25) return { color: '#f97316', label: 'WARNING - STRONG WIND', level: 'warning' };
  if (mph >= 20) return { color: '#eab308', label: 'CAUTION - GUSTY', level: 'caution' };
  if (mph >= 10) return { color: '#22c55e', label: 'MODERATE', level: 'moderate' };
  return { color: '#4ade80', label: 'CALM', level: 'calm' };
};

// Check for flash flood risk
const getFlashFloodRisk = (rain1h?: number, rain3h?: number): { risk: 'low' | 'moderate' | 'high' | 'extreme'; message: string } => {
  const rainRate = rain1h || 0;
  const rain3hTotal = rain3h || 0;
  
  if (rainRate >= 2 || rain3hTotal >= 4) {
    return { risk: 'extreme', message: '⚠️ EXTREME FLASH FLOOD RISK - Seek high ground immediately!' };
  }
  if (rainRate >= 1 || rain3hTotal >= 2) {
    return { risk: 'high', message: '⚠️ HIGH flash flood risk - Avoid low areas and washes!' };
  }
  if (rainRate >= 0.5 || rain3hTotal >= 1) {
    return { risk: 'moderate', message: 'Moderate flood risk - Be cautious near dry washes' };
  }
  return { risk: 'low', message: 'Low flood risk' };
};

export function WeatherOverlay({ latitude, longitude, darkMode: _darkMode = true, expanded = false, onToggleExpanded }: WeatherOverlayProps) {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isExpanded, setIsExpanded] = useState(expanded);

  const fetchWeather = useCallback(async () => {
    if (!latitude || !longitude) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Use our Google Weather API proxy
      const weatherUrl = `/api/google/weather?lat=${latitude}&lng=${longitude}&hourly=true`;
      const weatherRes = await fetch(weatherUrl);
      
      if (!weatherRes.ok) {
        const errorData = await weatherRes.json().catch(() => ({}));
        if (weatherRes.status === 500 && errorData.error?.includes('not configured')) {
          throw new Error('Weather API not configured');
        }
        throw new Error(errorData.error || 'Weather data unavailable');
      }
      
      const apiResponse = await weatherRes.json();
      
      // Check for thunderstorm/flood alerts from forecast
      const alerts: WeatherAlert[] = [];
      
      // Check if thunderstorm probability is high
      if (apiResponse.current.thunderstormProbability > 50) {
        alerts.push({
          event: 'Thunderstorm Warning',
          sender: 'Google Weather',
          start: Date.now(),
          end: Date.now() + 3600000,
          description: `${apiResponse.current.thunderstormProbability}% chance of thunderstorms. Seek shelter and avoid open areas.`,
          tags: ['thunderstorm', 'lightning'],
        });
      }
      
      // Check precipitation probability for flood risk
      if (apiResponse.current.precipProbability > 70) {
        alerts.push({
          event: 'Heavy Rain Alert',
          sender: 'Google Weather',
          start: Date.now(),
          end: Date.now() + 3600000,
          description: `${apiResponse.current.precipProbability}% precipitation probability. Monitor for flash flood conditions.`,
          tags: ['rain', 'flood'],
        });
      }
      
      // Estimate rain amounts from hourly forecast if available
      let rain1h = 0;
      let rain3h = 0;
      if (apiResponse.hourly && apiResponse.hourly.length > 0) {
        rain1h = apiResponse.hourly[0]?.precipAmount || 0;
        rain3h = apiResponse.hourly.slice(0, 3).reduce((sum: number, h: { precipAmount?: number }) => sum + (h.precipAmount || 0), 0);
      }
      
      const data: WeatherData = {
        temp: apiResponse.current.temp,
        feelsLike: apiResponse.current.feelsLike,
        humidity: apiResponse.current.humidity,
        windSpeed: apiResponse.current.windSpeed,
        windGust: apiResponse.current.windGust,
        windDirection: apiResponse.current.windDirection,
        windCardinal: apiResponse.current.windCardinal || getWindDirection(apiResponse.current.windDirection),
        description: apiResponse.current.description,
        icon: apiResponse.current.icon,
        visibility: apiResponse.current.visibility,
        clouds: apiResponse.current.clouds,
        uvIndex: apiResponse.current.uvIndex,
        pressure: apiResponse.current.pressure,
        precipProbability: apiResponse.current.precipProbability,
        thunderstormProbability: apiResponse.current.thunderstormProbability,
        isDaytime: apiResponse.current.isDaytime,
        heatIndex: apiResponse.current.heatIndex,
        windChill: apiResponse.current.windChill,
        rain1h,
        rain3h,
        alerts,
      };
      
      setWeather(data);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Weather fetch failed');
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude]);

  // Fetch weather on mount and when location changes
  useEffect(() => {
    fetchWeather();
    
    // Refresh every 10 minutes
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWeather]);

  const handleToggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onToggleExpanded?.();
  };

  if (error) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 88,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1050,
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          backdropFilter: 'blur(12px)',
          borderRadius: 2,
          border: `2px solid ${cyber.neonRed}60`,
          px: 2,
          py: 1,
        }}
      >
        <Typography sx={{ color: cyber.neonRed, fontSize: '0.75rem' }}>
          ⚠️ {error}
        </Typography>
      </Box>
    );
  }

  if (!weather) {
    return loading ? (
      <Box
        sx={{
          position: 'absolute',
          top: 88,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1050,
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          backdropFilter: 'blur(12px)',
          borderRadius: 2,
          px: 2,
          py: 1,
        }}
      >
        <LinearProgress sx={{ width: 100 }} />
      </Box>
    ) : null;
  }

  const windSeverity = getWindSeverity(weather.windSpeed);
  const floodRisk = getFlashFloodRisk(weather.rain1h, weather.rain3h);
  const hasAlerts = weather.alerts && weather.alerts.length > 0;
  const hasFloodAlerts = weather.alerts?.some(a => 
    a.event.toLowerCase().includes('flood') || 
    a.event.toLowerCase().includes('flash')
  );
  const hasWindWarning = windSeverity.level === 'caution' || windSeverity.level === 'warning' || windSeverity.level === 'danger' || windSeverity.level === 'extreme';
  
  // Determine border color based on most severe condition
  const borderColor = windSeverity.level === 'danger' || windSeverity.level === 'extreme' || hasFloodAlerts || floodRisk.risk === 'extreme'
    ? '#ef4444' // Red
    : windSeverity.level === 'warning' || floodRisk.risk === 'high'
    ? '#f97316' // Orange
    : windSeverity.level === 'caution' || floodRisk.risk === 'moderate' || hasAlerts
    ? '#eab308' // Yellow
    : cyber.neonCyan; // Default cyan

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 88,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1050,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(12px)',
        borderRadius: 3,
        border: `2px solid ${borderColor}60`,
        boxShadow: `0 4px 20px rgba(0, 0, 0, 0.4), 0 0 15px ${borderColor}30`,
        minWidth: 200,
        maxWidth: 380,
        overflow: 'hidden',
      }}
    >
      {/* Wind Warning Banner - Shows when wind >= 20 mph */}
      {hasWindWarning && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 0.75,
            px: 1.5,
            backgroundColor: `${windSeverity.color}20`,
            borderBottom: `1px solid ${windSeverity.color}40`,
            animation: windSeverity.level === 'danger' || windSeverity.level === 'extreme' ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 },
            },
          }}
        >
          <AirIcon sx={{ 
            fontSize: 16, 
            color: windSeverity.color,
            animation: 'spin 3s linear infinite',
            '@keyframes spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
          }} />
          <Typography sx={{ 
            color: windSeverity.color, 
            fontWeight: 700, 
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {windSeverity.label} • {weather.windSpeed} MPH
            {weather.windGust && weather.windGust > weather.windSpeed && ` (Gusts ${weather.windGust})`}
          </Typography>
        </Box>
      )}

      {/* Compact Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
        }}
        onClick={handleToggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Temperature */}
          <Tooltip title={`Feels like ${weather.feelsLike}°F`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ThermostatIcon sx={{ fontSize: 18, color: weather.temp > 100 ? cyber.neonRed : weather.temp < 32 ? cyber.neonBlue : cyber.neonGreen }} />
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
                {weather.temp}°F
              </Typography>
            </Box>
          </Tooltip>

          {/* Wind */}
          <Tooltip title={`Wind: ${weather.windSpeed} mph ${getWindDirection(weather.windDirection)}${weather.windGust ? ` (gusts ${weather.windGust} mph)` : ''}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AirIcon sx={{ fontSize: 18, color: windSeverity.color }} />
              <Typography sx={{ color: windSeverity.color, fontWeight: 700, fontSize: '0.9rem' }}>
                {weather.windSpeed}
              </Typography>
            </Box>
          </Tooltip>

          {/* Flash Flood Warning */}
          {(floodRisk.risk !== 'low' || weather.rain1h) && (
            <Tooltip title={floodRisk.message}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FloodIcon sx={{ 
                  fontSize: 18, 
                  color: floodRisk.risk === 'extreme' ? '#ff0000' : 
                         floodRisk.risk === 'high' ? '#ff4500' : 
                         floodRisk.risk === 'moderate' ? '#ffd700' : cyber.neonBlue,
                  animation: floodRisk.risk === 'extreme' || floodRisk.risk === 'high' ? 'pulse 1s infinite' : 'none',
                }} />
                {weather.rain1h && (
                  <Typography sx={{ 
                    color: floodRisk.risk === 'extreme' ? '#ff0000' : '#ffd700', 
                    fontWeight: 700, 
                    fontSize: '0.8rem' 
                  }}>
                    {weather.rain1h.toFixed(1)}"
                  </Typography>
                )}
              </Box>
            </Tooltip>
          )}

          {/* Alert indicator */}
          {hasAlerts && (
            <Tooltip title={`${weather.alerts!.length} weather alert(s)`}>
              <WarningAmberIcon sx={{ 
                fontSize: 18, 
                color: hasFloodAlerts ? '#ff0000' : '#ffd700',
                animation: 'pulse 1s infinite',
              }} />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            size="small" 
            onClick={(e) => { e.stopPropagation(); fetchWeather(); }}
            disabled={loading}
            sx={{ color: cyber.textSecondary, p: 0.5 }}
          >
            <RefreshIcon sx={{ fontSize: 16, animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
          {isExpanded ? (
            <ExpandLessIcon sx={{ fontSize: 18, color: cyber.textSecondary }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18, color: cyber.textSecondary }} />
          )}
        </Box>
      </Box>

      {/* Expanded Details */}
      <Collapse in={isExpanded}>
        <Box sx={{ px: 1.5, pb: 1.5, borderTop: `1px solid ${cyber.borderDefault}` }}>
          {/* Weather alerts */}
          {weather.alerts && weather.alerts.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {weather.alerts.slice(0, 3).map((alert, idx) => (
                <Alert 
                  key={idx}
                  severity={alert.event.toLowerCase().includes('flood') ? 'error' : 'warning'}
                  icon={alert.event.toLowerCase().includes('flood') ? <FloodIcon /> : <StormIcon />}
                  sx={{ 
                    mb: 0.5, 
                    py: 0, 
                    fontSize: '0.7rem',
                    backgroundColor: alert.event.toLowerCase().includes('flood') ? 'rgba(255, 0, 0, 0.15)' : 'rgba(255, 152, 0, 0.15)',
                    '& .MuiAlert-message': { py: 0.5 },
                  }}
                >
                  <strong>{alert.event}</strong>
                  <Typography sx={{ fontSize: '0.65rem', color: cyber.textSecondary }}>
                    {alert.description.substring(0, 100)}...
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}

          {/* Flash Flood Risk Banner */}
          {floodRisk.risk !== 'low' && (
            <Alert 
              severity={floodRisk.risk === 'extreme' ? 'error' : floodRisk.risk === 'high' ? 'error' : 'warning'}
              icon={<FloodIcon />}
              sx={{ 
                mt: 1,
                py: 0.5,
                backgroundColor: floodRisk.risk === 'extreme' ? 'rgba(255, 0, 0, 0.2)' : 
                                 floodRisk.risk === 'high' ? 'rgba(255, 69, 0, 0.2)' : 'rgba(255, 215, 0, 0.15)',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {floodRisk.message}
              </Typography>
            </Alert>
          )}

          {/* Detailed Stats */}
          <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WaterDropIcon sx={{ fontSize: 14, color: cyber.neonBlue }} />
              <Typography sx={{ fontSize: '0.7rem', color: cyber.textSecondary }}>
                Humidity: {weather.humidity}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: cyber.textSecondary }}>
                Visibility: {weather.visibility} mi
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: cyber.textSecondary }}>
                Clouds: {weather.clouds}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: cyber.textSecondary }}>
                Wind: {getWindDirection(weather.windDirection)}
              </Typography>
            </Box>
          </Box>

          {/* Wind severity bar */}
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: cyber.textSecondary }}>
                Wind Speed
              </Typography>
              <Chip 
                label={windSeverity.label} 
                size="small" 
                sx={{ 
                  height: 18, 
                  fontSize: '0.6rem', 
                  fontWeight: 700,
                  backgroundColor: `${windSeverity.color}30`,
                  color: windSeverity.color,
                  border: `1px solid ${windSeverity.color}60`,
                }} 
              />
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min((weather.windSpeed / 60) * 100, 100)}
              sx={{
                height: 6,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: windSeverity.color,
                  borderRadius: 1,
                },
              }}
            />
            {weather.windGust && (
              <Typography sx={{ fontSize: '0.65rem', color: cyber.textMuted, mt: 0.25 }}>
                Gusts up to {weather.windGust} mph
              </Typography>
            )}
          </Box>

          {/* Last update */}
          <Typography sx={{ fontSize: '0.6rem', color: cyber.textMuted, mt: 1, textAlign: 'center' }}>
            Updated: {lastUpdate?.toLocaleTimeString()}
          </Typography>
        </Box>
      </Collapse>

      {/* CSS Animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
}

export default WeatherOverlay;
